import { createTheme, PaletteMode, ThemeProvider } from "@mui/material";
import { ThemeProviderProps } from "@mui/material/styles/ThemeProvider";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ConfirmProvider } from "material-ui-confirm";
import { PropsWithChildren, useMemo, useState } from "react";
import { AlertProvider } from "./contexts/alert.context";
import { AuthorizeProvider } from "./contexts/authorize.context";
import { AxiosInstanceProvider } from "./contexts/axios.context";
import { ColorModeContext } from "./contexts/color.context";
import { LoadingProvider } from "./contexts/loading.context";
import ProviderComposer, { provide } from "./contexts/provider.compose";
import { ScreenProvider } from "./contexts/screen.context";
import { getDesignTokens } from "./themes";

const AppProvider = ({ children }: PropsWithChildren<{}>) => {
    const [mode, setMode] = useState<PaletteMode>("dark");
    const colorMode = useMemo(
        () => ({
            toggleColorMode: () => {
                setMode((prevMode: PaletteMode) => (prevMode === "light" ? "dark" : "light"));
            },
        }),
        []
    );

    const theme = useMemo(() => createTheme(getDesignTokens(mode)), [mode]);
    return (
        <ProviderComposer
            providers={[
                provide(LoadingProvider),
                provide(ColorModeContext.Provider, { value: colorMode }),
                provide(AlertProvider),
                provide(ScreenProvider),
                provide(LocalizationProvider, { dateAdapter: AdapterDayjs }),
                provide<ThemeProviderProps>(ThemeProvider, { theme }),
                provide(ConfirmProvider),
                provide(AuthorizeProvider),
                provide(AxiosInstanceProvider, { config: { baseURL: process.env.REACT_APP_API } }),
            ]}
        >
            {children}
        </ProviderComposer>
    );
};

export default AppProvider;
